import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, StatCard, Badge, Button, PageHeader } from '../../components/ui';
import api from '../../services/api';

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [recentClients, setRecentClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const res = await api.get('/superadmin/dashboard');
      setStats(res.data.stats || {});
      setRecentClients(res.data.recentClients || []);
    } catch {} finally { setLoading(false); }
  };

  const statusColor = { active: 'success', grace_period: 'warning', pending_payment: 'info', suspended: 'danger', deleted: 'gray' };
  const planColor = { pack_one: '#2ECC71', pack_two: '#3498DB', pack_advanced: '#F39C12' };

  return (
    <div>
      <PageHeader title="Admin Dashboard" subtitle="Overview of EmailMark platform" action={<Link to="/admin/clients"><Button>+ Add Client</Button></Link>} />

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard icon="👥" label="Total Clients" value={stats.totalClients || 0} />
        <StatCard icon="✅" label="Active Clients" value={stats.activeClients || 0} color="var(--success)" />
        <StatCard icon="💰" label="Total Revenue" value={`€${(stats.totalRevenue || 0).toLocaleString()}`} color="#F39C12" />
        <StatCard icon="📅" label="This Month" value={`€${(stats.monthRevenue || 0).toLocaleString()}`} color="#3498DB" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 28 }}>
        <StatCard icon="⚠️" label="Grace Period" value={stats.gracePeriodClients || 0} color="#F39C12" sub="Need attention" />
        <StatCard icon="⏳" label="Pending Payment" value={stats.pendingPayment || 0} color="#3498DB" />
        <StatCard icon="🚫" label="Suspended" value={stats.suspendedClients || 0} color="#E74C3C" />
        <StatCard icon="🗑️" label="Recoverable" value={stats.deletedRecoverable || 0} color="#888" sub="Within 48hrs" />
      </div>

      {/* Plan Breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        <Card>
          <h3 style={{ fontWeight: 700, marginBottom: 16 }}>📦 Clients by Plan</h3>
          {(stats.planBreakdown || []).map(p => (
            <div key={p.planId || p._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              <span style={{ fontSize: 14, color: planColor[p.planId || p._id] || '#fff', fontWeight: 600 }}>{(p.planId || p._id)?.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
              <span style={{ fontSize: 20, fontWeight: 800 }}>{p.count}</span>
            </div>
          ))}
          {(!stats.planBreakdown || stats.planBreakdown.length === 0) && <p style={{ color: '#555', fontSize: 13 }}>No active clients</p>}
        </Card>

        {/* Quick Actions */}
        <Card>
          <h3 style={{ fontWeight: 700, marginBottom: 16 }}>⚡ Quick Actions</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Link to="/admin/clients?action=create"><Button fullWidth>+ Create New Client</Button></Link>
            <Link to="/admin/plans"><Button fullWidth variant="secondary">✏️ Edit Plan Prices</Button></Link>
            <Link to="/admin/deleted"><Button fullWidth variant="secondary">🗑️ View Recoverable Clients</Button></Link>
            <Link to="/admin/revenue"><Button fullWidth variant="secondary">💰 Revenue Report</Button></Link>
          </div>
        </Card>
      </div>

      {/* Recent Clients */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontWeight: 700 }}>👥 Recent Clients</h3>
          <Link to="/admin/clients"><Button size="sm" variant="ghost">View All →</Button></Link>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                {['Name','Email','Company','Plan','Status','Expires','Action'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#555', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentClients.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                  <td style={{ padding: '12px', color: '#fff', fontWeight: 600 }}>{c.name}</td>
                  <td style={{ padding: '12px', color: '#888' }}>{c.email}</td>
                  <td style={{ padding: '12px', color: '#888' }}>{c.company}</td>
                  <td style={{ padding: '12px' }}><span style={{ color: planColor[c.planId] || '#fff', fontWeight: 600, fontSize: 12 }}>{c.planId?.replace('_',' ')}</span></td>
                  <td style={{ padding: '12px' }}><Badge color={statusColor[c.status] || 'gray'}>{c.status}</Badge></td>
                  <td style={{ padding: '12px', color: '#666', fontSize: 12 }}>{c.planExpiresAt ? new Date(c.planExpiresAt).toLocaleDateString() : '-'}</td>
                  <td style={{ padding: '12px' }}><Link to={`/admin/clients?id=${c.id}`}><Button size="sm" variant="ghost">Edit</Button></Link></td>
                </tr>
              ))}
            </tbody>
          </table>
          {recentClients.length === 0 && <div style={{ textAlign: 'center', padding: 32, color: '#555' }}>No clients yet</div>}
        </div>
      </Card>
    </div>
  );
}
